import React from "react";
import { Link, useLocation } from "react-router-dom";
import { appRoutes } from "@/routes";
import { listSections } from "@/core/global/SectionRegistry";

// Lightweight customizable config
type CustomLink = { path: string; label?: string; includeQuery?: boolean };
interface LinkGroup { title?: string; links: CustomLink[] }
interface PreviewSitemapConfig {
  // merge: show custom first then dynamic unique ones; customOnly: only custom; dynamicOnly: only dynamic
  mode: "merge" | "customOnly" | "dynamicOnly";
  includeAppRoutes: boolean;
  includeSectionRoutes: boolean;
  // when set, ensures this param=true exists in query string (e.g., "Preview")
  forcePreviewParam?: string;
  groups: LinkGroup[];
}

const CONFIG: PreviewSitemapConfig = {
  mode: "merge",
  includeAppRoutes: true,
  includeSectionRoutes: true,
  forcePreviewParam: "Preview",
  groups: [
    {
      title: "Primary",
      links: [
        { path: "/", label: "Home" },
        { path: "/dashboard", label: "Dashboard" },
        { path: "/dashboard/profile?step=personal", label: "Profile - Personal" },
      ],
    },
    {
      title: "Key Flows",
      links: [
        { path: "/dashboard/documents", label: "Documents" },
        { path: "/dashboard/financial", label: "Financial" },
        { path: "/dashboard/program-enrollment", label: "Program Enrollment" },
      ],
    },
  ],
};

const PreviewSitemap: React.FC = () => {
  const location = useLocation();
  const currentQs = new URLSearchParams(location.search);

  // Optionally force a specific Preview param
  if (CONFIG.forcePreviewParam) {
    currentQs.set(CONFIG.forcePreviewParam, "true");
  }
  const qsString = currentQs.toString();

  // Dynamic links
  const sections = CONFIG.includeSectionRoutes ? listSections() : [];
  const sectionLinks = (CONFIG.includeSectionRoutes ? sections.map(s => s.route).filter(Boolean) : []) as string[];
  const routeSet = new Set<string>();
  if (CONFIG.includeAppRoutes) {
    for (const r of appRoutes) routeSet.add(r.path);
  }
  if (CONFIG.includeSectionRoutes) {
    for (const r of sectionLinks) routeSet.add(r);
  }
  const dynamicRoutes = Array.from(routeSet).sort();

  // Custom links (flatten groups)
  const customLinksFlat: CustomLink[] = CONFIG.groups.flatMap(g => g.links);
  const customPathSet = new Set(customLinksFlat.map(c => c.path.split("? ")[0] || c.path));

  // Build merged list when needed
  const mergedDynamic: Array<{ path: string; label?: string }> = [];
  if (CONFIG.mode !== "customOnly") {
    for (const p of dynamicRoutes) {
      // skip ones already present in custom (match by pathname ignoring query to avoid dup)
      const pBase = p.split("?")[0];
      if (customPathSet.has(pBase)) continue;
      mergedDynamic.push({ path: p, label: p });
    }
  }

  return (
    <div className="bg-slate-900/95 border-slate-700 z-[9998] fixed left-0 top-0 h-full w-64 overflow-y-auto border-r">
      <div className="border-slate-700 bg-slate-900/95 sticky top-0 border-b px-4 py-3">
        <h2 className="text-white text-sm font-semibold">Preview Sitemap</h2>
        <p className="text-slate-400 text-xs">Links to all pages and sub-pages</p>
      </div>

      <nav className="space-y-3 p-3">
        {/* Custom groups */}
        {CONFIG.mode !== "dynamicOnly" && CONFIG.groups.map((group, gi) => (
          <div key={`grp-${gi}`}>
            {group.title && (
              <div className="text-slate-400 mb-1 px-3 text-[11px] uppercase tracking-wider">
                {group.title}
              </div>
            )}
            <div className="space-y-1">
              {group.links.map((lnk, i) => {
                const to = `${lnk.path}${(lnk.includeQuery ?? true) && qsString ? `?${qsString}` : ""}`;
                return (
                  <Link key={`${lnk.path}-${i}`} to={to} className="text-slate-200 block rounded px-3 py-2 text-sm hover:bg-slate-800">
                    {lnk.label || lnk.path}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        {/* Dynamic list appended when merging */}
        {CONFIG.mode === "merge" && mergedDynamic.length > 0 && (
          <div>
            <div className="text-slate-400 mb-1 px-3 text-[11px] uppercase tracking-wider">All Routes</div>
            <div className="space-y-1">
              {mergedDynamic.map((r) => {
                const to = `${r.path}${qsString ? `?${qsString}` : ""}`;
                return (
                  <Link key={`dyn-${r.path}`} to={to} className="text-slate-200 block rounded px-3 py-2 text-sm hover:bg-slate-800">
                    {r.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Pure dynamic only mode */}
        {CONFIG.mode === "dynamicOnly" && (
          <div className="space-y-1">
            {dynamicRoutes.map((path) => {
              const to = `${path}${qsString ? `?${qsString}` : ""}`;
              return (
                <Link key={path} to={to} className="text-slate-200 block rounded px-3 py-2 text-sm hover:bg-slate-800">
                  {path}
                </Link>
              );
            })}
          </div>
        )}
      </nav>
    </div>
  );
};

export default PreviewSitemap;
